import React from 'react';
import { Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import useResponsiveness from '@/hooks/useResponsiveness';
import DropdownMenuItemDivider from '@/components/DropdownMenu/DropdownMenuItemDivider';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import { useTranslation } from '@/hooks/useTranslation';

function CollectionDropdown({
  TriggerComponent,
  noRoot,
  handleSettings,
  handleAddFiles,
  handleCreateTask,
  handleChangeIcon,
  handleRename,
  handleDelete,
  handleNewSubcollection,
  handleMove,
  isLongPress,
  title,
  align,
  side,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();

  const content = (
    <>
      {TriggerComponent && (
        <DropdownMenu.Trigger action={isLongPress ? 'longPress' : undefined}>
          <TriggerComponent />
        </DropdownMenu.Trigger>
      )}
      <DropdownMenu.Content 
        className={Platform.OS === 'web' ? "DropdownMenuContent" : undefined}
        side={side}
        align={align || (isXS ? 'center' : 'end')} 
        sideOffset={isXS ? 4 : 5}
      >
        {Platform.OS === 'web' ? (
          // Web implementation
          <>
            {title && (
              <>
                <DropdownMenuLabel key="collection-title" text={title} />
                <DropdownMenuItemDivider key="collection-title-divider" />
              </>
            )}
            {handleSettings && (
              <DropdownMenuItem
                key="settings"
                onSelect={() => handleSettings?.()}
                className="DropdownMenuItem"
                icon="gear"
                title={t('pages.collections.settings')}
              />
            )}
            {handleAddFiles && (
              <DropdownMenuItem
                key="add-files"
                onSelect={() => handleAddFiles?.()}
                className="DropdownMenuItem"
                icon="file-circle-plus"
                title={t('pages.collections.addFiles')}
              />
            )}
            {handleCreateTask && (
              <DropdownMenuItem
                key="create-task"
                onSelect={() => handleCreateTask?.()}
                className="DropdownMenuItem"
                icon="clock"
                title={t('pages.collections.createTask')}
              />
            )}
            {(handleSettings || handleAddFiles || handleCreateTask) && (
              <DropdownMenuItemDivider key="collection-actions-divider" />
            )}
            <DropdownMenuItem
              key="change-icon"
              onSelect={() => handleChangeIcon?.()}
              className="DropdownMenuItem"
              icon="icons"
              title={t('dropdowns.changeIcon')}
            />
            {handleNewSubcollection ? (
              <DropdownMenuItem
                key="new-subcollection"
                onSelect={() => handleNewSubcollection?.()}
                className="DropdownMenuItem"
                icon="folder-plus"
                title={t('dropdowns.newSubcollection')}
              />
            ) : null}
            {handleMove ? (
              <DropdownMenuItem
                key="move-collection"
                onSelect={() => handleMove?.()}
                className="DropdownMenuItem"
                icon="folder-arrow-down"
                title={t('dropdowns.moveCollection')}
              />
            ) : null}
            <DropdownMenuItem
              key="rename"
              onSelect={() => handleRename?.()}
              className="DropdownMenuItem"
              icon="pencil"
              title={t('dropdowns.renameCollection')}
            />
            <DropdownMenuItem
              key="delete"
              onSelect={() => handleDelete?.()}
              className="DropdownMenuItem"
              icon="trash"
              title={t('dropdowns.deleteCollection')}
              isDanger={true}
            />
          </>
        ) : (
          // Native implementation
          <>
            {title && (
              <>
                <DropdownMenu.Item key="collection-title" disabled>
                  <DropdownMenu.ItemTitle numberOfLines={1}>{title}</DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
              </>
            )}
            {handleSettings && (
              <DropdownMenu.Item key="settings" onSelect={() => handleSettings?.()}>
                <DropdownMenu.ItemIcon ios={{
                  name: "gearshape",
                  color: colors.text_color_primary
                }} />
                <DropdownMenu.ItemTitle>
                  {t('pages.collections.settings')}
                </DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            )}
            {handleAddFiles && (
              <DropdownMenu.Item key="add-files" onSelect={() => handleAddFiles?.()}>
                <DropdownMenu.ItemIcon ios={{
                  name: "doc.badge.plus",
                  color: colors.text_color_primary
                }} />
                <DropdownMenu.ItemTitle>
                  {t('pages.collections.addFiles')}
                </DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            )}
            {handleCreateTask && (
              <DropdownMenu.Item key="create-task" onSelect={() => handleCreateTask?.()}>
                <DropdownMenu.ItemIcon ios={{
                  name: "clock",
                  color: colors.text_color_primary
                }} />
                <DropdownMenu.ItemTitle>
                  {t('pages.collections.createTask')}
                </DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            )}
            {(handleSettings || handleAddFiles || handleCreateTask) && (
              <DropdownMenu.Separator />
            )}
            <DropdownMenu.Item key="change-icon" onSelect={() => handleChangeIcon?.()}>
              <DropdownMenu.ItemIcon ios={{ 
                name: "square.grid.2x2",
                color: colors.text_color_primary 
              }} />
              <DropdownMenu.ItemTitle>
                {t('dropdowns.changeIcon')}
              </DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
            {handleNewSubcollection ? (
              <DropdownMenu.Item key="new-subcollection" onSelect={() => handleNewSubcollection?.()}>
                <DropdownMenu.ItemIcon ios={{
                  name: "folder.badge.plus",
                  color: colors.text_color_primary
                }} />
                <DropdownMenu.ItemTitle>
                  {t('dropdowns.newSubcollection')}
                </DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            ) : null}
            {handleMove ? (
              <DropdownMenu.Item key="move-collection" onSelect={() => handleMove?.()}>
                <DropdownMenu.ItemIcon ios={{
                  name: "folder",
                  color: colors.text_color_primary
                }} />
                <DropdownMenu.ItemTitle>
                  {t('dropdowns.moveCollection')}
                </DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            ) : null}
            <DropdownMenu.Item key="rename" onSelect={() => handleRename?.()}>
              <DropdownMenu.ItemIcon ios={{ 
                name: "pencil",
                color: colors.text_color_primary 
              }} />
              <DropdownMenu.ItemTitle>
                {t('dropdowns.renameCollection')}
              </DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
            <DropdownMenu.Item key="delete" destructive onSelect={() => handleDelete?.()}>
              <DropdownMenu.ItemIcon ios={{
                name: "trash",
              }} />
              <DropdownMenu.ItemTitle>
                {t('dropdowns.deleteCollection')}
              </DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
          </>
        )}
      </DropdownMenu.Content>
    </>
  );

  if (noRoot) {
    return content;
  }

  return (
    <DropdownMenu.Root>
      {content}
    </DropdownMenu.Root>
  );
}

export default CollectionDropdown;